
import StyleProvider from '@/StyleProvider.vue';
import TheApp from './TheApp.vue';
import Vue, { PropType } from 'vue';
import i18n from '@/i18n';
import isValidLanguageValidator from '@/validators/langLocaleValidator';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useAuthStore } from '@/stores/v2/auth';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { pinia } from '@/pinia';
import { mapActions, mapState, mapStores } from 'pinia';
import { ProductPrintPositionAddModel } from '@/typings';
import { ComputedRef } from 'vue';
import { PDPPrintPositionsModel } from '@/typings/ProductPrintData';
import { generateEventPayload } from '@/utils/generatePriceParamsEventPayload';
import { SupportedLangCodes } from '../../constants/languageCodes';
import { createStateSnapshot } from '@/utils/historyHelper';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

declare module 'vue/types/vue' {
  interface Vue {
    $printPositions: ComputedRef;
  }
}
export default Vue.extend({
  components: {
    StyleProvider,
    TheApp,
  },
  inject: {
    $printPositions: {
      default: [],
    },
  },
  props: {
    sapCustomerId: {
      type: String,
      required: false,
      default: '',
    },
    tok: {
      type: String,
      required: true,
    },
    languageCode: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'en',
      validator: isValidLanguageValidator,
    },
    editId: {
      type: String,
      required: false,
      default: '',
    },
    productSku: {
      type: String,
      required: false,
      default: '',
    },
    variantSku: {
      type: String,
      required: false,
      default: '',
    },
    context: {
      type: String,
      required: true,
      default: 'pdp',
    },
    /**
     * If needed initially occupied positions with their respective techniques
     * can be specified
     */
    initialPositions: {
      type: [] as PropType<PDPPrintPositionsModel[]>,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      positions: [] as ProductPrintPositionAddModel[],
    };
  },
  computed: {
    ...mapStores(useProductStore),
    ...mapStores(useHistoryStore),
    ...mapState(useAuthStore, ['isTokValid']),
    ...mapState(useProductStore, [
      'data',
      'unavailablePrintPositions',
      'positionsSetFromProps',
      'isLaserConverter',
    ]),
    ...mapState(useShapeStore, ['positionShapeMap']),
    ...mapState(useMirrorStore, ['positionMirrorSettings']),
  },
  watch: {
    isTokValid(newValue) {
      if (!newValue) {
        this.$emit('token-invalid-or-expired');
      }
    },
    async tok(newValue) {
      if (newValue !== '') {
        this.setTok(this.tok);
        await this.setDefaultTranslations();
      }
    },
    async initialPositions() {
      if (this.context !== 'wizard') {
        await this.initializeFromPdpContext();
        this.positions = [...this.unavailablePrintPositions];
      }
    },
  },
  async mounted() {
    this.setLanguageLocale(this.languageCode);
    this.setSapCustomerId(this.sapCustomerId);
    this.setTok(this.tok);
    await this.setDefaultTranslations();

    if (this.context !== 'wizard') {
      /**
       *  Used when this component is initialised on it's own
       */
      await this.initializeFromPdpContext();
    } else {
      /**
       * Used when initialisation is done in a context of a wizard component
       */
      console.warn('$printPositions Injection(): ', this.$printPositions.value);

      this.setUnavailablePrintPositions(this.unavailablePrintPositions);
    }

    // Pass the newly set unavailablePrintPositions to the positions array that will be passed to the child component
    this.positions = [...this.unavailablePrintPositions];
  },
  methods: {
    ...mapActions(useLangStore, [
      'setLanguageLocale',
      'setDefaultTranslations',
    ]),
    ...mapActions(useAuthStore, ['setSapCustomerId', 'setTok']),
    ...mapActions(useProductStore, [
      'getProduct',
      'setUnavailablePrintPositions',
      'setActivePrintPosition',
    ]),
    ...mapActions(useMirrorStore, ['addPositionMirrorSettings']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useNotificationStore, ['runValidators']),

    onCloseModal() {
      this.$modal.hide('print-positions-modal');

      this.$emit('print-position:cancel');
    },
    async initializeFromPdpContext() {
      const pdpPositions = [] as ProductPrintPositionAddModel[];

      if (this.productSku) {
        await this.getProduct(this.productSku).then((result) => {
          this.initialPositions.map((initPosition) => {
            const printPosition = result?.productPrintPositions.find((p) => {
              return p.name === initPosition.printPositionSAPCode;
            });

            if (printPosition) {
              const printTechnique = printPosition.printTechniques.find((t) => {
                return (
                  t.printTechnique.code === initPosition.printTechniqueSAPCode
                );
              });

              if (printTechnique) {
                pdpPositions.push({
                  positionId: printPosition.id,
                  positionName: printPosition.name,
                  printPositionSAPCode: printPosition.name,
                  maxHeightMm: printPosition.maxHeightMm,
                  maxWidthMm: printPosition.maxWidthMm,
                  technique: {
                    id: printTechnique.id,
                    printTechniqueSAPCode: printTechnique.printTechnique.code,
                    techniqueName: printTechnique.printTechnique.name,
                    groupCode: printTechnique.printTechnique.group.code,
                  },
                });
              }
            }
          });
        });
      }

      // Reset the unavailablePrintPositions array depending on the users chosen print positions on the PDP page
      this.setUnavailablePrintPositions(pdpPositions);
    },

    savePositions(
      value: ProductPrintPositionAddModel[],
      lastSwap: { from: string; to: string }
    ) {
      this.positions = value;
      this.setUnavailablePrintPositions(value);

      this.positions.forEach((p) => {
        const idx = this.positionMirrorSettings.findIndex(
          (x) => x.positionId === p.positionId
        );
        if (idx === -1) {
          this.addPositionMirrorSettings(p.positionId, false, false);
        }
      });

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      this.setActivePrintPosition(value[value.length - 1]);

      if (this.editId !== '' && this.editId !== EMPTY_GUID) {
        this.productStore.$patch((state) => {
          const exists = state.editedPositions.some((id) => id === this.editId);
          if (!exists) {
            state.editedPositions.push(this.editId);
          }
        });

        this.$emit(
          'wizard:print-position-edit',
          value,
          lastSwap.to,
          lastSwap.from
        );
      }

      this.$emit('print-position:action', [...value]);

      this.runValidators();

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },
  },
  i18n,
  pinia,
});
